// ═══ REBALANCING ENGINE ═══
// Soft-only rebalancing: DCA redirect, no selling
// Scope: Retirement + Crypto + SAP XETRA (excludes TRY cash, trades, reserve fund)
// Schedule: May end + November end (aligned with Q1/Q3 bonus)

import { S } from '../core/state.js';
import { hVal, hTotUSD } from '../core/math.js';
import { toUSD, fU, fP, esc, now, uid } from '../core/utils.js';

// ─── ASSET TYPE MAPPING ───
// Maps holding types to rebalancing categories
const TYPE_MAP = {
  'US ETF': 'US ETF',
  'US Equity': 'US Stock',
  'BIST Fund': 'TR Fund',
  'BIST Equity': 'TR Stock',
  'Commodity': 'Commodity',
  'Cash': 'Cash',
  // Fallbacks
  'ETF': 'US ETF',
  'Stock': 'US Stock',
  'Fund': 'TR Fund',
};

// ─── DYNAMIC PORTFOLIO DETECTION ───
// Finds retirement portfolio ID by name match, all others are top-level portfolios
function _getRetirementPid() {
  if (!S.portfolios) return null;
  const match = S.rebalancing?.retirementMatch || 'retirement';
  const ret = S.portfolios.find(p => p.name && p.name.toLowerCase().includes(match) && p.status === 'active');
  return ret ? ret.id : null;
}

function _getTopLevelPortfolios() {
  if (!S.portfolios) return [];
  const retPid = _getRetirementPid();
  return S.portfolios.filter(p => p.status === 'active' && p.id !== retPid);
}

function _portfolioKey(p) {
  // Normalize portfolio name to a key for topLevel config lookup
  return p.name.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/xetra|portfolio|medium|term|long|short/g, '').trim() || p.id;
}

/**
 * Category for a holding, per Model A (the S.rebalancing.targets band model).
 * Exported so the bonus engine can resolve Model B instruments INSIDE a Model A
 * category without duplicating the mapping. (audit 2026-08-01)
 */
export function holdingCategory(h) { return _mapType(h); }

function _mapType(h) {
  const retPid = _getRetirementPid();

  // If holding belongs to a non-retirement portfolio → top-level category
  if (h.portfolioId && h.portfolioId !== retPid) {
    const portfolio = S.portfolios?.find(p => p.id === h.portfolioId);
    if (portfolio) return '_TL:' + _portfolioKey(portfolio); // top-level prefix
    return null;
  }

  // Retirement sub-allocation
  const t = h.type || 'Other';
  if (t === 'Stock' || t === 'Equity') {
    return (h.currency === 'USD') ? 'US Stock' : (h.currency === 'EUR') ? null : 'TR Stock';
  }
  if (t === 'ETF') return 'US ETF';
  if (t === 'Fund') return 'TR Fund';
  if (t === 'Commodity') return 'Commodity';
  if (t === 'Cash') return 'Cash';
  return null;
}

// ─── CALCULATE CURRENT ALLOCATION ───
export function calcDrift() {
  const rb = S.rebalancing;
  if (!rb) return null;

  // `|| 1` valued every TRY holding at 1 TRY = 1 USD, so category weights, driftPP and
  // the rebalance action targetUSD were all computed against a ~40x-inflated TR sleeve.
  // Without a rate there is no meaningful drift to report. (audit 2026-08-01)
  const fx = S.fxRate > 0 ? S.fxRate : null;
  const fxEUR = S.fxRateEUR > 0 ? S.fxRateEUR : null;
  if (fx == null) return null;

  // 1. All holdings by category (retirement sub-alloc + top-level portfolios)
  const allTypes = {};
  let retTotal = 0;
  const topLevelValues = {}; // key → USD value for each non-retirement portfolio

  S.holdings.forEach(h => {
    const cat = _mapType(h);
    if (!cat) return;

    const qty = h.qty || (h.lots ? h.lots.reduce((s, l) => s + (l.qty || 0), 0) : 0);
    let valUSD;

    // Cash-type: qty IS the currency amount
    if (h.type === 'Cash' && (h.ticker === 'USD' || h.ticker === 'EUR')) {
      valUSD = h.ticker === 'USD' ? qty : qty * fxEUR / fx;
    } else if (h.currency === 'EUR') {
      const p = S.prices[h.ticker]?.price || 0;
      valUSD = (p * qty) * fxEUR / fx;
    } else if (h.currency === 'USD') {
      const p = S.prices[h.ticker]?.price || 0;
      valUSD = p * qty;
    } else {
      const p = S.prices[h.ticker]?.price || 0;
      valUSD = (p * qty) / fx;
    }

    // Top-level portfolio (non-retirement)
    if (cat.startsWith('_TL:')) {
      const key = cat.slice(4);
      topLevelValues[key] = (topLevelValues[key] || 0) + valUSD;
    } else {
      // Retirement sub-allocation
      if (!allTypes[cat]) allTypes[cat] = { value: 0, items: [] };
      allTypes[cat].value += valUSD;
      allTypes[cat].items.push({ ticker: h.ticker, value: valUSD });
      retTotal += valUSD;
    }
  });

  // Top-level analysis — retirement = 100 units base, each other portfolio measured relative to it
  const retUnits = 100;
  const topLevelAnalysis = {};
  const topLevelConfig = rb.topLevel || {};

  // Auto-register new portfolios with default band
  _getTopLevelPortfolios().forEach(p => {
    const key = _portfolioKey(p);
    if (!topLevelConfig[key]) {
      topLevelConfig[key] = { lo: 80, hi: 150, emergencyLo: 65, emergencyHi: 180 };
      rb.topLevel = topLevelConfig; // persist new entry
    }
    const value = topLevelValues[key] || 0;
    const units = retTotal > 0 ? (value / retTotal) * 100 : 0;
    const band = topLevelConfig[key];
    const status = value === 0 ? 'empty' : units < band.lo ? 'below' : units > band.hi ? 'above' : 'in_band';
    // `id` and `sizing` travel with the row so consumers can tell a target from a description.
    // A band is a WEIGHTED-sizing concept: it says where a sleeve should be steered back to.
    // An `accumulate` sleeve is one the owner has instructed the app never to argue with, so a
    // ceiling on it is a contradiction -- and it is exactly what SAP XETRA is. (2026-09-02)
    topLevelAnalysis[key] = { id: p.id, sizing: p.sizing || null, name: p.name, value, units, band, status };
  });

  // Backward compat
  const sapValue = topLevelValues['sap'] || 0;
  const cryptoValue = topLevelValues['crypto'] || topLevelValues['cryptomedium'] || 0;
  const sapUnits = retTotal > 0 ? (sapValue / retTotal) * 100 : 0;
  const cryptoUnits = retTotal > 0 ? (cryptoValue / retTotal) * 100 : 0;

  // 5. Retirement sub-allocation drift
  const drifts = [];
  const targets = rb.targets || {};
  Object.entries(targets).forEach(([cat, band]) => {
    const data = allTypes[cat] || { value: 0, items: [] };
    const currentPct = retTotal > 0 ? (data.value / retTotal) * 100 : 0;
    const midTarget = (band.lo + band.hi) / 2;
    const driftPP = currentPct - midTarget;
    const status = currentPct < band.lo ? 'below' : currentPct > band.hi ? 'above' : 'in_band';
    const targetValue = retTotal * midTarget / 100;
    const actionUSD = targetValue - data.value;

    drifts.push({
      category: cat,
      currentValue: data.value,
      currentPct,
      targetLo: band.lo,
      targetHi: band.hi,
      midTarget,
      driftPP,
      status,
      actionUSD, // positive = need to buy more, negative = overweight
      items: data.items
    });
  });

  // Sort: biggest drift first
  drifts.sort((a, b) => Math.abs(b.driftPP) - Math.abs(a.driftPP));

  // 6. Emergency checks
  const emergencies = [];
  const tl = rb.topLevel || {};

  // Retirement drift emergency
  drifts.forEach(d => {
    if (Math.abs(d.driftPP) >= (rb.emergencyDriftPP || 20)) {
      emergencies.push({ type: 'drift', category: d.category, driftPP: d.driftPP, message: `${d.category} drift ${d.driftPP > 0 ? '+' : ''}${d.driftPP.toFixed(1)}pp exceeds ±${rb.emergencyDriftPP}pp threshold` });
    }
  });

  // Top-level portfolio band emergencies (dynamic — covers SAP, Crypto, and any future portfolio)
  Object.entries(topLevelAnalysis).forEach(([key, tla]) => {
    // An accumulate sleeve has no band to breach. Owner, 2026-09-02: "272 EUR'a gelene kadar
    // almaya devam edeceğim" -- the standing instruction runs until its own trigger and nothing
    // else. Raising a band emergency on it would be the app arguing with the one decision it
    // was told not to touch. Its price trigger is its only voice.
    // ...and neither has a `cyclical` one, as of 2026-09-03. The owner removed the crypto
    // ceiling outright: size follows the halving clock, and a band expressed as a percent of
    // the RETIREMENT sleeve moved every time retirement grew, which is not a fact about crypto
    // at all. The approved K3 phase-1 plan ($5,350) breached the 70-unit ceiling on its own in
    // month one and nothing had ever checked the two against each other.
    if (tla.sizing === 'accumulate' || tla.sizing === 'cyclical') return;
    if (tla.units > 0 && tla.band) {
      if (tla.units < tla.band.emergencyLo || tla.units > tla.band.emergencyHi) {
        emergencies.push({ type: 'portfolio_band', key, name: tla.name, units: tla.units, message: `${tla.name} at ${tla.units.toFixed(0)} units — outside ${tla.band.emergencyLo}-${tla.band.emergencyHi} emergency band` });
      }
    }
  });

  // 7. Review due check
  const today = new Date();
  const month = today.getMonth() + 1; // 1-indexed
  const day = today.getDate();
  const reviewMonths = rb.reviewMonths || [5, 11];
  const reviewDay = rb.reviewDayStart || 28;
  const isReviewWindow = reviewMonths.includes(month) && day >= reviewDay;

  // Check if review already done this period
  const lastReview = rb.reviews?.length ? rb.reviews[rb.reviews.length - 1] : null;
  const lastReviewMonth = lastReview ? new Date(lastReview.date).getMonth() + 1 : 0;
  const lastReviewYear = lastReview ? new Date(lastReview.date).getFullYear() : 0;
  const reviewDone = isReviewWindow && lastReviewMonth === month && lastReviewYear === today.getFullYear();
  const reviewDue = isReviewWindow && !reviewDone;

  // Universe total = retirement + all top-level portfolios
  const tlTotal = Object.values(topLevelValues).reduce((s, v) => s + v, 0);

  const result = {
    // Top level — dynamic
    retTotal,
    topLevelAnalysis,
    topLevelValues,
    universeTotal: retTotal + tlTotal,
    retUnits,
    // Backward compat for dashboard UI
    sapValue,
    cryptoValue,
    sapUnits,
    cryptoUnits,
    // Retirement sub-allocation
    drifts,
    // Emergency
    emergencies,
    hasEmergency: emergencies.length > 0,
    // Review
    isReviewWindow,
    reviewDue,
    reviewDone,
    lastReview,
    // DCA lock
    dcaLocked: rb.dcaLocked || false,
    // Timestamp
    ts: Date.now()
  };

  // Store in state for AI side panel access
  S.rebalancing.lastDrift = result;
  S.rebalancing.lastDriftTs = Date.now();

  return result;
}

// ─── CHECK IF DCA SHOULD BE LOCKED ───
export function checkDCALock() {
  const rb = S.rebalancing;
  if (!rb) return false;

  const today = new Date();
  const month = today.getMonth() + 1;
  const day = today.getDate();
  const reviewMonths = rb.reviewMonths || [5, 11];
  const reviewDay = rb.reviewDayStart || 28;

  // Lock DCA if we're in review window and review not done
  if (reviewMonths.includes(month) && day >= reviewDay) {
    const lastReview = rb.reviews?.length ? rb.reviews[rb.reviews.length - 1] : null;
    const lastReviewMonth = lastReview ? new Date(lastReview.date).getMonth() + 1 : 0;
    const lastReviewYear = lastReview ? new Date(lastReview.date).getFullYear() : 0;
    const reviewDone = lastReviewMonth === month && lastReviewYear === today.getFullYear();

    if (!reviewDone) {
      rb.dcaLocked = true;
      rb.dcaLockedReason = `Rebalance review due — complete review before ${month === 5 ? 'May' : 'November'}-end DCA`;
      return true;
    }
  }

  rb.dcaLocked = false;
  rb.dcaLockedReason = null;
  return false;
}

// ─── GENERATE DCA REDIRECT SUGGESTIONS ───
export function generateRebalanceSuggestions(drift) {
  if (!drift) return [];

  const suggestions = [];
  const monthlyDCA = S.base || 2400; // monthly DCA budget
  const monthsToNextReview = 6; // soft rebalance horizon: 6 months
  const above = drift.drifts.filter(d => d.status === 'above').sort((a, b) => b.driftPP - a.driftPP);
  const below = drift.drifts.filter(d => d.status === 'below').sort((a, b) => a.driftPP - b.driftPP);
  const inBand = drift.drifts.filter(d => d.status === 'in_band');

  // Calculate how much budget to redirect: pause overweight categories → fund underweight
  let freedBudget = 0;
  above.forEach(d => {
    const currentDCAShare = d.currentPct / 100 * monthlyDCA;
    // Pause or reduce DCA for overweight categories
    const monthsToSkip = Math.min(Math.ceil(Math.abs(d.actionUSD) / currentDCAShare), monthsToNextReview);
    freedBudget += currentDCAShare;
    suggestions.push({
      category: d.category,
      action: 'pause',
      severity: Math.abs(d.driftPP) >= 5 ? 'high' : 'medium',
      currentPct: d.currentPct,
      targetMid: d.midTarget,
      driftPP: d.driftPP,
      currentDCA: Math.round(currentDCAShare),
      message: `Pause ${d.category} DCA for ${monthsToSkip} month${monthsToSkip > 1 ? 's' : ''} ($${Math.round(currentDCAShare)}/mo freed)`,
      detail: `Currently ${d.currentPct.toFixed(1)}% vs target ${d.targetLo}-${d.targetHi}%. Over by ${Math.abs(d.driftPP).toFixed(1)}pp ($${Math.round(Math.abs(d.actionUSD))}). Natural drift through pausing will bring it back within ${monthsToSkip} months.`
    });
  });

  // Distribute freed budget to underweight categories proportionally
  const totalDeficit = below.reduce((s, d) => s + Math.abs(d.actionUSD), 0);
  below.forEach(d => {
    const deficit = Math.abs(d.actionUSD);
    const currentDCAShare = d.currentPct / 100 * monthlyDCA;
    const extraPerMonth = totalDeficit > 0 ? (deficit / totalDeficit) * freedBudget : 0;
    const newMonthlyDCA = currentDCAShare + extraPerMonth;
    const monthsToTarget = extraPerMonth > 0 ? Math.ceil(deficit / extraPerMonth) : monthsToNextReview;
    suggestions.push({
      category: d.category,
      action: 'boost',
      severity: Math.abs(d.driftPP) >= 5 ? 'high' : 'medium',
      currentPct: d.currentPct,
      targetMid: d.midTarget,
      driftPP: d.driftPP,
      currentDCA: Math.round(currentDCAShare),
      newDCA: Math.round(newMonthlyDCA),
      extraPerMonth: Math.round(extraPerMonth),
      monthsToTarget: Math.min(monthsToTarget, monthsToNextReview),
      message: `Boost ${d.category} DCA: $${Math.round(currentDCAShare)}/mo → $${Math.round(newMonthlyDCA)}/mo (+$${Math.round(extraPerMonth)}) for ${Math.min(monthsToTarget, monthsToNextReview)} months`,
      detail: `Currently ${d.currentPct.toFixed(1)}% vs target ${d.targetLo}-${d.targetHi}%. Under by ${Math.abs(d.driftPP).toFixed(1)}pp ($${Math.round(deficit)} gap). Extra $${Math.round(extraPerMonth)}/mo redirected from paused categories.`
    });
  });

  // In-band categories: maintain current DCA
  inBand.forEach(d => {
    const currentDCAShare = d.currentPct / 100 * monthlyDCA;
    suggestions.push({
      category: d.category,
      action: 'maintain',
      severity: 'low',
      currentPct: d.currentPct,
      targetMid: d.midTarget,
      driftPP: d.driftPP,
      currentDCA: Math.round(currentDCAShare),
      message: `Maintain ${d.category} at $${Math.round(currentDCAShare)}/mo — within band`,
      detail: `Currently ${d.currentPct.toFixed(1)}% (target ${d.targetLo}-${d.targetHi}%). No change needed.`
    });
  });

  // Top-level portfolio suggestions
  Object.entries(drift.topLevelAnalysis || {}).forEach(([_key, tla]) => {
    if (tla.status === 'empty') {
      suggestions.push({
        category: tla.name,
        action: 'open',
        severity: 'high',
        message: `Open ${tla.name} position — currently 0 units (target ${tla.band.lo}-${tla.band.hi})`,
        detail: `No holdings in ${tla.name} portfolio. To reach ${tla.band.lo} units you need ~$${Math.round(drift.retTotal * tla.band.lo / 100)} invested.`
      });
    } else if (tla.status === 'below') {
      suggestions.push({
        category: tla.name,
        action: 'boost',
        severity: 'medium',
        message: `${tla.name} at ${tla.units.toFixed(0)} units — increase DCA toward ${tla.band.lo} unit minimum`,
        detail: `Need $${Math.round(drift.retTotal * (tla.band.lo - tla.units) / 100)} more to reach lower band.`
      });
    } else if (tla.status === 'above') {
      suggestions.push({
        category: tla.name,
        action: 'pause',
        severity: 'medium',
        message: `${tla.name} at ${tla.units.toFixed(0)} units — pause DCA until retirement catches up`,
        detail: `Above ${tla.band.hi} unit cap. Let retirement grow to bring ratio back in band.`
      });
    }
  });

  // Sort: high severity first, then pause → boost → maintain → open
  const actionOrder = { pause: 0, boost: 1, open: 2, maintain: 3 };
  const sevOrder = { high: 0, medium: 1, low: 2 };
  suggestions.sort((a, b) => (sevOrder[a.severity] || 2) - (sevOrder[b.severity] || 2) || (actionOrder[a.action] || 3) - (actionOrder[b.action] || 3));

  return suggestions;
}

// ─── COMPLETE REVIEW ───
export function completeReview(notes) {
  const rb = S.rebalancing;
  if (!rb) return;

  const drift = calcDrift();
  const review = {
    date: new Date().toISOString(),
    driftSnapshot: drift?.drifts?.map(d => ({
      category: d.category,
      currentPct: Math.round(d.currentPct * 10) / 10,
      status: d.status,
      driftPP: Math.round(d.driftPP * 10) / 10
    })),
    topLevel: {
      retTotal: Math.round(drift?.retTotal || 0),
      sapUnits: Math.round((drift?.sapUnits || 0) * 10) / 10,
      cryptoUnits: Math.round((drift?.cryptoUnits || 0) * 10) / 10
    },
    notes: notes || '',
    emergencies: drift?.emergencies?.length || 0
  };

  if (!rb.reviews) rb.reviews = [];
  rb.reviews.push(review);

  // Unlock DCA
  rb.dcaLocked = false;
  rb.dcaLockedReason = null;

  return review;
}

// ─── ACTION TRACKER ───
// Convert approved suggestions into trackable actions
export function activateActions(suggestions) {
  const rb = S.rebalancing;
  if (!rb) return;
  // Clear previous active actions
  rb.activeActions = [];
  suggestions.forEach(s => {
    if (s.action === 'maintain') return; // no action needed for in-band
    rb.activeActions.push({
      // Was a second, WEAKER copy of uid(): slice(2, 6) is FOUR base-36 chars — 36^4 = 1,679,616,
      // thirty-six times less than the generator that took the deploy gate down — minted inside a
      // forEach, so every id in a batch shares one millisecond. Two `find(a => a.id === actionId)`
      // call sites below take the first match and orphan the second. Standing rule #22: the rule
      // lives in a function, and this call site now uses it.
      id: uid(),
      category: s.category,
      type: s.action, // pause, boost, open
      targetUSD: s.amountUSD || (s.action === 'boost' ? Math.abs(s.targetMid ? (s.targetMid - s.currentPct) / 100 * (S.rebalancing?.lastDrift?.retTotal || 0) : 0) : 0),
      progressUSD: 0,
      monthlyExtra: s.extraPerMonth || 0,
      monthsPlanned: s.monthsToTarget || 6,
      status: 'active', // active → completed
      message: s.message,
      detail: s.detail || '',
      createdAt: new Date().toISOString(),
      completedAt: null
    });
  });
}

// Record progress on an active action (called when DCA purchase is made)
export function trackActionProgress(actionId, amountUSD) {
  const rb = S.rebalancing;
  if (!rb) return;
  const action = (rb.activeActions || []).find(a => a.id === actionId);
  if (!action || action.status !== 'active') return;
  action.progressUSD += amountUSD;
  if (action.targetUSD > 0 && action.progressUSD >= action.targetUSD) {
    action.status = 'completed';
    action.completedAt = new Date().toISOString();
  }
}

// Mark an action as completed manually
export function completeAction(actionId) {
  const rb = S.rebalancing;
  if (!rb) return;
  const action = (rb.activeActions || []).find(a => a.id === actionId);
  if (!action) return;
  action.status = 'completed';
  action.completedAt = new Date().toISOString();
}

// Get active (non-completed) actions
export function getActiveActions() {
  return (S.rebalancing?.activeActions || []).filter(a => a.status === 'active');
}

// ─── EXPORTS FOR MAIN.JS ───
export { _mapType };
